import { useCallback, type ChangeEvent } from 'react';
import { NUMERIC_INPUT_MAX_DIGITS } from '@/lib/constants/numeric-input';
import { caretIndexForDigits, groupDigits, sanitizeNumericInput, toWholeDigits } from '@/lib/utils/numeric.utils';

export type NumericInputValue = string | number | null | undefined;

interface UseNumericInputOptions {
  /** The plain value the form holds (digit string or saved number). */
  value: NumericInputValue;
  /** The field's change handler. Receives the event with `target.value` set to the plain digits. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Group thousands in what the user sees. Default true. */
  groupThousands?: boolean;
  /** Most digits the field accepts; extra digits are ignored. Default `NUMERIC_INPUT_MAX_DIGITS`. */
  maxDigits?: number;
}

interface NumericInputBindings {
  /** What the input shows (grouped digits). */
  displayValue: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Behaviour of the numeric `TextField` (MYN-277, FR-183): the user can only ever
 * enter digits, the text is regrouped as they type, the caret stays at the digit
 * they were typing next to, and the form holds the plain digit string.
 *
 * The handler hands the parent the event with `target.value` temporarily set to
 * the plain digits (so `field.onChange(event)` stores `5050505`, not `5,050,505`),
 * then writes the grouped text and caret back. The input is controlled, so the
 * re-render shows the same grouped text and React leaves the caret alone.
 */
export function useNumericInput({
  value,
  onChange,
  groupThousands = true,
  maxDigits = NUMERIC_INPUT_MAX_DIGITS,
}: UseNumericInputOptions): NumericInputBindings {
  const format = groupThousands ? groupDigits : (digits: string) => digits;
  // A saved decimal shows rounded to whole units, never with its dot stripped (1500.25 -> 1,500).
  const displayValue = format(toWholeDigits(value));

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const caret = input.selectionStart ?? input.value.length;
      const { digits, caretDigits } = sanitizeNumericInput(input.value, caret, maxDigits);
      const shown = groupThousands ? groupDigits(digits) : digits;

      input.value = digits;
      onChange?.(event);
      input.value = shown;
      const position = caretIndexForDigits(shown, caretDigits);
      input.setSelectionRange(position, position);
    },
    [groupThousands, maxDigits, onChange],
  );

  return { displayValue, onChange: handleChange };
}
